import { useDeploymentMode } from "../../../hooks/useDeploymentMode";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { AppButton } from "../../../components/form";
import { PageEmptyState } from "../../../components/Page";
import authService from "../../../services/authService";
import { workspaceService, type WorkspaceHomeSummary } from "../../../services/workspaceService";
import { platformAdminService, type PlatformStats } from "../../../services/platformAdminService";
import { getCurrentUserRoles, isAdmin, isTenantAdmin } from "../../../utils/authorization";
import { deriveAttentionItems, greetingKey, quotaRatio, QUOTA_WARN_RATIO } from "../homeSummary";
import HomeGettingStarted from "./HomeGettingStarted";
import { enter, Glyph, IconTile, Row, Section, Tile, type Tone } from "./parts";

const SEVERITY: Record<"error" | "warning" | "info", { icon: string; tone: Tone }> = {
  error: { icon: "error", tone: "error" },
  warning: { icon: "warning", tone: "warning" },
  info: { icon: "info", tone: "info" },
};

function formatDate(value?: string | null) {
  return value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "–";
}

export default function HomePage() {
  const { onPrem } = useDeploymentMode();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<WorkspaceHomeSummary | null>(null);
  const [platform, setPlatform] = useState<PlatformStats | null>(null);
  const [platformAdmin, setPlatformAdmin] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [userId, setUserId] = useState("");
  const [showGettingStarted, setShowGettingStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [roles, user] = await Promise.all([getCurrentUserRoles(), authService.getUser()]);
      const profile: any = (user as any)?.profile ?? {};
      setDisplayName(profile.given_name || profile.name || profile.preferred_username || "");
      setUserId(String(profile.sub || profile.preferred_username || profile.email || "user"));
      const admin = isAdmin(roles);
      setPlatformAdmin(admin);
      // Plan: tenant_admin only; exclude platform super-admin home.
      setShowGettingStarted(isTenantAdmin(roles) && !admin);
      if (admin) {
        setPlatform(await platformAdminService.platformStats());
      } else {
        setSummary(await workspaceService.homeSummary());
      }
    } catch (e: any) {
      setError(e?.message || t("home.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const attention = useMemo(() => deriveAttentionItems(summary), [summary]);
  const limitLabel = (key: string) => t(`home.limits.${key}`, { defaultValue: key });

  const hero = (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: { xs: 2, sm: 3 },
        // Mica-like wash: faint accent over the neutral surface, never behind small text.
        backgroundImage: `radial-gradient(900px 260px at 0% 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.08)}, transparent 70%)`,
        ...enter(0),
      })}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <IconTile name={platformAdmin ? "admin_panel_settings" : "home"} size={56} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" component="h1" sx={{ lineHeight: 1.25, fontSize: { xs: "1.35rem", sm: undefined } }}>
              {t(`home.greeting.${greetingKey()}`)}
              {displayName ? `, ${displayName}` : ""}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body1" color="text.secondary">
                {platformAdmin
                  ? t("home.subtitlePlatform")
                  : summary?.workspaceName
                    ? t("home.subtitleWorkspace", { name: summary.workspaceName })
                    : t("home.subtitle")}
              </Typography>
              {summary?.limits ? (
                <Chip size="small" variant="outlined" label={t("home.limits.package", { name: t(`packageLimits.source.${summary.limits.packageType}`, { defaultValue: summary.limits.packageType }) })} />
              ) : null}
            </Stack>
          </Box>
        </Stack>
        <AppButton variant="outlined" onClick={() => void load()} disabled={loading} startIcon={<Glyph name="refresh" fontSize="small" />}>
          {t("common.refresh")}
        </AppButton>
      </Stack>
    </Paper>
  );

  if (loading && !summary && !platform) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 1180, mx: "auto" }}>
        {hero}
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} aria-label={t("common.loading")} />
        </Box>
      </Stack>
    );
  }

  if (platformAdmin) {
    const currency = platform?.currency || "EUR";
    return (
      <Stack spacing={2.5} sx={{ maxWidth: 1180, mx: "auto" }}>
        {hero}
        {error && <Alert severity="error">{error}</Alert>}
        <Section
          index={1}
          title={t("home.platform.title")}
          description={t("home.platform.description")}
          action={
            <AppButton variant="outlined" onClick={() => navigate("/super-admin")} endIcon={<Glyph name="chevron_right" fontSize="small" />}>
              {t("home.platform.open")}
            </AppButton>
          }
        >
          <Grid container>
            {[
              { icon: "group", tone: "primary" as Tone, label: t("superAdmin.dashboard.totalUsers"), value: platform?.totalUsers ?? 0 },
              { icon: "how_to_reg", tone: "success" as Tone, label: t("superAdmin.dashboard.activeUsers"), value: platform?.activeUsers ?? 0 },
              {
                icon: "payments",
                tone: "info" as Tone,
                label: t("superAdmin.dashboard.supportRevenue"),
                value: `${Number(platform?.supportRevenueThisMonth ?? 0).toFixed(2)} ${currency}`,
              },
              {
                icon: "support_agent",
                tone: "warning" as Tone,
                label: t("superAdmin.dashboard.activeSupport"),
                value: Object.values(platform?.activeSupportByPackage ?? {}).reduce((a, b) => a + b, 0),
              },
            ]
              .filter((k) => !onPrem || k.icon === "group" || k.icon === "how_to_reg")
              .map((k) => (
              <Grid key={k.label} size={{ xs: 6, md: 3 }} sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={1.25}>
                  <IconTile name={k.icon} tone={k.tone} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {k.label}
                    </Typography>
                    <Typography variant="h3" component="div" sx={{ lineHeight: 1.2, fontSize: { xs: "1.25rem", sm: undefined } }}>
                      {k.value}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Section>
      </Stack>
    );
  }

  const s = summary;
  const recentDashboards = s?.content?.recentDashboards ?? [];
  const recentIncidents = s?.anomalies?.recent ?? [];

  const tiles: { to: string; icon: string; tone: Tone; label: string; value: number; hint?: string }[] = [];
  if (s?.content) {
    tiles.push(
      { to: "/dashboards", icon: "dashboard", tone: "primary", label: t("home.kpi.dashboards"), value: s.content.dashboards },
      { to: "/widgets", icon: "widgets", tone: "info", label: t("home.kpi.widgets"), value: s.content.widgets },
      { to: "/widgets?tab=queries", icon: "manage_search", tone: "secondary", label: t("home.kpi.queries"), value: s.content.dashboardQueries }
    );
  }
  if (s?.anomalies) {
    tiles.push({
      to: "/anomalies?tab=incidents",
      icon: "notification_important",
      tone: s.anomalies.openIncidents > 0 ? "error" : "success",
      label: t("home.kpi.openIncidents"),
      value: s.anomalies.openIncidents,
      hint: t("home.kpi.acknowledged", { count: s.anomalies.acknowledgedIncidents }),
    });
  }
  if (s?.dataPipeline) {
    tiles.push(
      {
        to: "/data-sources?tab=ingest",
        icon: "upload_file",
        tone: s.dataPipeline.ingestFailed7d > 0 ? "warning" : "success",
        label: t("home.kpi.ingest7d"),
        value: s.dataPipeline.ingestCompleted7d,
        hint: t("home.kpi.failed", { count: s.dataPipeline.ingestFailed7d }),
      },
      {
        to: "/data-prep",
        icon: "account_tree",
        tone: s.dataPipeline.prepFailedRuns7d > 0 ? "warning" : "primary",
        label: t("home.kpi.prepJobs"),
        value: s.dataPipeline.prepJobs,
        hint: t("home.kpi.failedRuns", { count: s.dataPipeline.prepFailedRuns7d }),
      }
    );
  }
  if (s?.connections) {
    tiles.push({
      to: "/data-sources?tab=adaptors",
      icon: "hub",
      tone: "info",
      label: t("home.kpi.connections"),
      value: s.connections.dataAdaptors + s.connections.openApiCatalogs,
      hint: t("home.kpi.connectionsHint", { adaptors: s.connections.dataAdaptors, catalogs: s.connections.openApiCatalogs }),
    });
  }
  if (s?.schedules) {
    tiles.push({
      to: "/insight-report-jobs",
      icon: "schedule",
      tone: s.schedules.failing > 0 ? "warning" : "secondary",
      label: t("home.kpi.schedules"),
      value: s.schedules.activeExportSchedules + s.schedules.enabledInsightJobs,
      hint: t("home.kpi.failing", { count: s.schedules.failing }),
    });
  }

  const quick = [
    { to: "/report", icon: "chat", label: t("home.quick.chat") },
    { to: "/dashboards", icon: "dashboard", label: t("home.quick.dashboards") },
    { to: "/widgets", icon: "widgets", label: t("home.quick.widgets") },
    ...(s?.dataPipeline ? [{ to: "/data-sources?tab=ingest", icon: "upload_file", label: t("home.quick.ingest") }] : []),
  ];

  const workspaceKey = s?.workspaceName || "workspace";

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1180, mx: "auto" }}>
      {hero}
      {error && <Alert severity="error">{error}</Alert>}

      {showGettingStarted && userId ? (
        <HomeGettingStarted userId={userId} workspaceId={workspaceKey} />
      ) : null}

      <Section index={1} title={t("home.attention.title")} description={t("home.attention.description")}>
        {attention.length === 0 ? (
          <Row icon="check_circle" tone="success" primary={t("home.attention.allClear")} />
        ) : (
          attention.map((item) => (
            <Row
              key={item.key}
              to={item.to}
              icon={SEVERITY[item.severity].icon}
              tone={SEVERITY[item.severity].tone}
              primary={t(`home.attention.items.${item.key}`, { count: item.count ?? 0 })}
              secondary={t(`home.attention.severity.${item.severity}`)}
            />
          ))
        )}
      </Section>

      {/* Single compact quick-actions row — avoids a second full section later on the page. */}
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
        {quick.map((a) => (
          <Chip
            key={a.to}
            component={RouterLink}
            to={a.to}
            clickable
            icon={<Glyph name={a.icon} fontSize="small" />}
            label={a.label}
            variant="outlined"
            sx={{ textDecoration: "none" }}
          />
        ))}
      </Stack>

      {tiles.length > 0 && (
        <Grid container spacing={1.5}>
          {tiles.map((tile, i) => (
            <Grid key={tile.to + tile.label} size={{ xs: 6, md: tiles.length <= 3 ? 4 : 3 }}>
              <Tile {...tile} index={i + 2} />
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: s?.anomalies ? 6 : 12 }}>
          <Section index={3} title={t("home.recentDashboards.title")}>
            {recentDashboards.length === 0 ? (
              <PageEmptyState message={t("home.recentDashboards.empty")} />
            ) : (
              recentDashboards.map((d) => (
                <Row
                  key={d.id}
                  to={`/dashboards/${d.id}`}
                  icon="dashboard"
                  primary={d.name}
                  secondary={formatDate(d.updatedAt)}
                  trailing={d.published ? <Chip size="small" color="primary" variant="outlined" label={t("home.recentDashboards.published")} /> : undefined}
                />
              ))
            )}
          </Section>
        </Grid>
        {s?.anomalies ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <Section index={4} title={t("home.recentIncidents.title")}>
              {recentIncidents.length === 0 ? (
                <PageEmptyState message={t("home.recentIncidents.empty")} />
              ) : (
                recentIncidents.map((i) => (
                  <Row
                    key={i.id}
                    to="/anomalies?tab=incidents"
                    icon="notification_important"
                    tone={i.status === "OPEN" ? "error" : "secondary"}
                    primary={i.definitionName || i.id}
                    secondary={formatDate(i.lastSeenAt)}
                    trailing={
                      <Chip
                        size="small"
                        variant="outlined"
                        color={i.status === "OPEN" ? "error" : "default"}
                        label={i.grade ? `${i.status} · ${i.grade}` : i.status}
                      />
                    }
                  />
                ))
              )}
            </Section>
          </Grid>
        ) : null}
      </Grid>

      {s?.limits ? (
        <Section index={5} title={t("home.limits.title")} description={t("home.limits.package", { name: t(`packageLimits.source.${s.limits.packageType}`, { defaultValue: s.limits.packageType }) })}>
          {s.limits.items.map((item) => {
            const ratio = quotaRatio(item);
            const near = ratio != null && ratio >= QUOTA_WARN_RATIO;
            return (
              <Row key={item.key} primary={limitLabel(item.key)}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={ratio == null ? 0 : Math.min(100, ratio * 100)}
                    color={near ? "warning" : "primary"}
                    aria-label={limitLabel(item.key)}
                    sx={{ flex: 1, height: 6, borderRadius: 3, opacity: ratio == null ? 0.35 : 1 }}
                  />
                  <Typography
                    variant="body2"
                    color={near ? "warning.main" : "text.secondary"}
                    fontWeight={near ? 700 : 400}
                    sx={{ minWidth: 84, textAlign: "right" }}
                  >
                    {item.used.toLocaleString()} / {item.max == null ? t("home.limits.unlimited") : item.max.toLocaleString()}
                  </Typography>
                </Stack>
              </Row>
            );
          })}
        </Section>
      ) : null}
    </Stack>
  );
}
